import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Brand, BrandProductsCount } from '../models/brand.entity'
import { BrandsService } from '../brands.service'
import { BrandsRepository } from '../brands.repository'
import { Page } from '../../../models/util.model'
import { BrandSearchParams } from '../models/brand-search.model'
import { BrandPageData } from '../models/brand-listing.model'
import { parseBrandsPage } from '../libs/brand-search.lib'

interface BrandsListingState {
  filters?: BrandSearchParams
  data?: BrandPageData[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const BRANDS_LISTING_INITIAL_STATE: BrandsListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class BrandsListingStore {
  constructor(
    private brandsService: BrandsService,
    private brandsRepository: BrandsRepository,
  ) {}

  private state = signal<BrandsListingState>(BRANDS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isErrored = computed(() => !!this.state().error)
  readonly brands = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly isFiltered = computed(() => !!this.state().filters)

  // Effects

  async search(params?: BrandSearchParams, returnAll?: boolean): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.brandsService.searchData$(params, returnAll).subscribe({
        next: (data) => {
          resolve()
          this.setData(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async delete(brand: Brand) {
    return new Promise<boolean>((resolve) => {
      this.brandsRepository.alert('BRAND_DELETION', brand).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.brandsService.delete$(brand._id).subscribe({
            next: () => {
              resolve(true)
              this.brandsRepository.notify({
                code: 'BRAND_DELETED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  // Reducers

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: BrandSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  addFilter(filter: BrandSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: BrandSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof BrandSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  setData(data: { page: Page<Brand>; productsCounts?: BrandProductsCount[] }) {
    this.state.set({
      ...this.state(),
      data: parseBrandsPage(data.page, data.productsCounts).data,
      totalCount: data.page.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
